// src/services/userService.js
import { objectToQueryString } from '../utils/apiUtils';
import { api } from './api';
import {
  createService,
  createNestedCollection
} from './factory';

// Create the base user service using the factory
const userServiceBase = createService('User', '/users', {
  nestedCollections: [
    createNestedCollection('permissions'),
    createNestedCollection('clients')
  ],
  customEndpoints: {
    // Get current user
    getCurrentUser: async () => {
      try {
        return await api.get('/auth/me');
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Login user
    login: async (email, password) => {
      try {
        return await api.post('/auth/login', { email, password });
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Logout user
    logout: async () => {
      try {
        return await api.post('/auth/logout');
      } catch (error) {
        // Ignore error on logout, just let the client clear the token
        return null;
      }
    },

    // Register user
    register: async (userData) => {
      try {
        return await api.post('/auth/register', userData);
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Request password reset
    forgotPassword: async (email) => {
      try {
        return await api.post('/auth/forgot-password', { email });
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Reset password with token
    resetPassword: async (token, newPassword) => {
      try {
        return await api.post('/auth/reset-password', { token, newPassword });
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Change password
    changePassword: async (oldPassword, newPassword) => {
      try {
        return await api.put('/auth/change-password', { oldPassword, newPassword });
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Assign user to clients
    assignUserToClients: async (userId, clientIds) => {
      try {
        return await api.put(`/users/${userId}/assign-clients`, { clientIds });
      } catch (error) {
        console.log(`Error assigning user ${userId} to clients:`, error);
        console.log('error', error);
      }
    },

    // Get users by client
    getUsersByClient: async (clientId, params = {}) => {
      try {
        const queryString = params instanceof URLSearchParams 
          ? `?${params.toString()}` 
          : objectToQueryString(params);
        return await api.get(`/users/client/${clientId}${queryString}`);
      } catch (error) {
        console.log(`Error fetching users for client ${clientId}:`, error);
        console.log('error', error);
      }
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getUsers = async (params = {}) => {
  try {
    return await userServiceBase.getAll(params);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getUserById = async (id) => {
  try {
    return await userServiceBase.getById(id);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const createUser = async (userData) => {
  try {
    return await userServiceBase.create(userData);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateUser = async (id, userData) => {
  try {
    return await userServiceBase.update(id, userData);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteUser = async (id) => {
  try {
    return await userServiceBase.delete(id);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getCurrentUser = async () => {
  return userServiceBase.getCurrentUser();
};

export const login = async (email, password) => {
  return userServiceBase.login(email, password);
};

export const logout = async () => {
  return userServiceBase.logout();
};

export const register = async (userData) => {
  return userServiceBase.register(userData);
};

export const forgotPassword = async (email) => {
  return userServiceBase.forgotPassword(email);
};

export const resetPassword = async (token, newPassword) => {
  return userServiceBase.resetPassword(token, newPassword);
};

export const changePassword = async (oldPassword, newPassword) => {
  return userServiceBase.changePassword(oldPassword, newPassword);
};

export const updateUserPermissions = async (userId, permissions) => {
  try {
    return await api.put(`/users/${userId}/permissions`, { permissions });
  } catch (error) {
    console.log(`Error updating permissions for user ${userId}:`, error);
    console.log('error', error);
  }
};

export const updateUserStatus = async (userId, active) => {
  try {
    return await userServiceBase.updateStatus(userId, active);
  } catch (error) {
    console.log(`Error updating status for user ${userId}:`, error);
    console.log('error', error);
  }
};

export const assignUserToClients = async (userId, clientIds) => {
  return userServiceBase.assignUserToClients(userId, clientIds);
};

export const getUsersByClient = async (clientId, params = new URLSearchParams()) => {
  return userServiceBase.getUsersByClient(clientId, params);
};

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  // Handle specific error codes
  if (error.response) {
    const { status, data } = error.response;

    if (status === 401) {
      // Unauthorized - token expired or invalid
      // You might want to handle token refresh or logout here
      return new Error('Your session has expired. Please login again.');
    }

    if (status === 403) {
      return new Error('You do not have permission to perform this action.');
    }

    if (status === 404) {
      return new Error('The requested user was not found.');
    }

    // Use error message from the API if available
    if (data && data.message) {
      return new Error(data.message);
    }
  }

  // Network errors or other issues
  if (error.request) {
    return new Error('Network error. Please check your connection.');
  }

  // Default error message
  return error;
};